<template>
  <div class="user-list-container">
    <div class="page-header">
      <div class="page-title-block">
        <h2>用户列表</h2>
        <p class="page-subtitle">团队成员与销售人员一目了然，助力农资服务协同</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="handleAddUser">
          <i class="el-icon-plus"></i> 新增用户
        </el-button>
      </div>
    </div>
    
    <!-- 搜索区域 -->
    <el-card class="search-card">
      <div class="search-form">
        <el-form :inline="true" :model="searchForm" class="search-inline-form">
          <el-form-item label="用户名称">
            <el-input v-model="searchForm.name" placeholder="请输入用户名称" clearable @input="handleSearch"></el-input>
          </el-form-item>
          <el-form-item label="手机号">
            <el-input v-model="searchForm.phone" placeholder="请输入手机号" clearable @input="handleSearch"></el-input>
          </el-form-item>
          <el-form-item label="部门">
            <el-select v-model="searchForm.departmentId" placeholder="请选择部门" clearable @change="handleSearch">
              <el-option v-for="dept in departments" :key="dept.id" :label="dept.name" :value="dept.id"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">
              <i class="el-icon-search"></i> 搜索
            </el-button>
            <el-button @click="handleReset">
              <i class="el-icon-refresh"></i> 重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
    
    <!-- 移动端卡片视图 -->
    <div v-if="isMobile" class="user-list-mobile">
      <div 
        v-for="(user, idx) in pagedUsers" 
        :key="user.id" 
        class="user-card"
      >
        <div class="user-info">
          <p class="user-serial">序号 {{ (currentPage - 1) * pageSize + idx + 1 }}</p>
          <h3>{{ user.name }}</h3>
          <p><i class="el-icon-user"></i> {{ user.username }}</p>
          <p><i class="el-icon-phone"></i> {{ user.phone }}</p>
          <p><i class="el-icon-message"></i> {{ user.email }}</p>
          <p><i class="el-icon-office-building"></i> 部门：{{ user.department && user.department.name ? user.department.name : '无' }}</p>
        </div>
        <div class="user-actions">
          <el-button type="primary" size="mini" @click="handleViewUser(user)">
            查看
          </el-button>
          <el-button type="success" size="mini" @click="handleEditUser(user)">
            编辑
          </el-button>
          <el-button type="danger" size="mini" @click="handleDeleteUser(user)">
            删除
          </el-button>
          <el-button size="mini" @click="openRoleDialog(user)">
            分配角色
          </el-button>
          <el-button size="mini" @click="handleChangePassword(user)">
            修改密码
          </el-button>
        </div>
      </div>
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          :hide-on-single-page="false"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount">
        </el-pagination>
      </div>
    </div>
    
    <!-- 桌面端表格视图 -->
    <el-card v-else>
      <el-table :data="pagedUsers" style="width: 100%" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column label="序号" width="72" align="center">
          <template slot-scope="scope">
            {{ (currentPage - 1) * pageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="name" label="用户名称" min-width="120"></el-table-column>
        <el-table-column prop="username" label="用户名" min-width="120"></el-table-column>
        <el-table-column prop="phone" label="手机号" min-width="150"></el-table-column>
        <el-table-column prop="email" label="邮箱" min-width="180"></el-table-column>
        <el-table-column prop="department.name" label="部门" min-width="120"></el-table-column>
        <el-table-column label="角色" min-width="180">
          <template slot-scope="scope">
            <el-tag
              v-for="role in (scope.row.roles || [])"
              :key="role.id"
              size="mini"
              type="success"
              class="role-tag"
              style="margin-right: 4px; margin-bottom: 2px;"
            >
              {{ role.name }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="260" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" size="small" @click="handleViewUser(scope.row)">
              查看
            </el-button>
            <el-button type="success" size="small" @click="handleEditUser(scope.row)">
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDeleteUser(scope.row)">
              删除
            </el-button>
            <el-button size="small" @click="openRoleDialog(scope.row)">
              分配角色
            </el-button>
            <el-button size="small" @click="handleChangePassword(scope.row)">
              修改密码
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          :hide-on-single-page="false"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount">
        </el-pagination>
      </div>
    </el-card>
    
    <!-- 新增/编辑用户对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" :width="isMobile ? '90%' : '600px'">
      <el-form :model="userForm" :rules="userRules" ref="userForm" label-width="100px">
        <el-form-item label="用户名称" prop="name">
          <el-input v-model="userForm.name" placeholder="请输入用户名称"></el-input>
        </el-form-item>
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" placeholder="请输入用户名"></el-input>
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="userForm.phone" placeholder="请输入手机号"></el-input>
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" placeholder="请输入邮箱"></el-input>
        </el-form-item>
        <el-form-item label="部门" prop="departmentId">
          <el-select v-model="userForm.departmentId" placeholder="请选择部门">
            <el-option v-for="dept in departments" :key="dept.id" :label="dept.name" :value="dept.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!isEditMode">
          <el-input v-model="userForm.password" type="password" placeholder="请输入密码"></el-input>
          <div class="password-strength">
            <div class="strength-bars">
              <span :class="['bar', addPasswordStrengthLevel >= 1 ? 'active' : '']"></span>
              <span :class="['bar', addPasswordStrengthLevel >= 2 ? 'active' : '']"></span>
              <span :class="['bar', addPasswordStrengthLevel >= 3 ? 'active' : '']"></span>
            </div>
            <span class="strength-text" :class="'level-' + addPasswordStrengthTextType">
              密码强度：{{ addPasswordStrengthText }}
            </span>
          </div>
          <div class="password-rule-hint">
            密码要求：6-20 位，必须同时包含字母、数字和符号。
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">提交</el-button>
      </span>
    </el-dialog>
    
    <!-- 修改密码对话框 -->
    <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" :width="isMobile ? '90%' : '400px'">
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="100px">
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码"></el-input>
          <div class="password-strength">
            <div class="strength-bars">
              <span :class="['bar', passwordStrengthLevel >= 1 ? 'active' : '']"></span>
              <span :class="['bar', passwordStrengthLevel >= 2 ? 'active' : '']"></span>
              <span :class="['bar', passwordStrengthLevel >= 3 ? 'active' : '']"></span>
            </div>
            <span class="strength-text" :class="'level-' + passwordStrengthTextType">
              密码强度：{{ passwordStrengthText }}
            </span>
          </div>
          <div class="password-rule-hint">
            密码要求：6-20 位，必须同时包含字母、数字和符号。
          </div>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请确认新密码"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handlePasswordSubmit">提交</el-button>
      </span>
    </el-dialog>

    <!-- 分配角色对话框 -->
    <el-dialog
      title="分配角色"
      :visible.sync="roleDialogVisible"
      :width="isMobile ? '90%' : '520px'"
    >
      <div class="role-list-box">
        <el-checkbox-group v-model="roleForm.roleIds">
          <el-checkbox
            v-for="role in allRoles"
            :key="role.id"
            :label="role.id"
          >
            {{ role.name }}（{{ role.code }}）
          </el-checkbox>
        </el-checkbox-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="roleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleRoleSubmit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'UserList',
  data() {
    return {
      users: [], // 用户列表
      filteredUsers: [], // 过滤后的用户列表
      departments: [], // 部门列表
      selectedUsers: [], // 选中的用户
      currentPage: 1, // 当前页码
      pageSize: 10, // 每页条数
      searchForm: { // 搜索条件
        name: '',
        phone: '',
        departmentId: ''
      },
      dialogVisible: false, // 新增/编辑对话框可见性
      passwordDialogVisible: false, // 修改密码对话框可见性
      isEditMode: false, // 是否为编辑模式
      dialogTitle: '', // 对话框标题
      isMobile: false, // 是否为移动端
      userForm: {
        id: '',
        name: '',
        username: '',
        phone: '',
        email: '',
        departmentId: '',
        password: ''
      },
      passwordForm: {
        userId: '',
        newPassword: '',
        confirmPassword: ''
      },
      // 角色分配
      roleDialogVisible: false,
      allRoles: [],
      roleForm: {
        userId: '',
        roleIds: []
      },
      userRules: {
        name: [
          { required: true, message: '请输入用户名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' },
          { required: true, message: '请输入邮箱地址', trigger: 'blur' }
        ],
        departmentId: [
          { required: true, message: '请选择部门', trigger: 'change' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { validator: this.validateStrongPassword, trigger: 'blur' }
        ]
      },
      passwordRules: {
        newPassword: [
          { validator: this.validateStrongPassword, trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: this.validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    // 修改密码弹窗中的密码强度（1：弱，2：中，3：强）
    passwordStrengthLevel() {
      return this.calcPasswordStrength(this.passwordForm.newPassword).level
    },
    passwordStrengthText() {
      return this.calcPasswordStrength(this.passwordForm.newPassword).text
    },
    passwordStrengthTextType() {
      return this.calcPasswordStrength(this.passwordForm.newPassword).type
    },
    // 新增用户弹窗中的密码强度（1：弱，2：中，3：强）
    addPasswordStrengthLevel() {
      return this.calcPasswordStrength(this.userForm.password).level
    },
    addPasswordStrengthText() {
      return this.calcPasswordStrength(this.userForm.password).text
    },
    addPasswordStrengthTextType() {
      return this.calcPasswordStrength(this.userForm.password).type
    },
    /** 与当前筛选结果一致，用于分页 total */
    totalCount() {
      return (this.filteredUsers && this.filteredUsers.length) || 0
    },
    /** 当前页数据（前端分页） */
    pagedUsers() {
      const list = this.filteredUsers || []
      const start = (this.currentPage - 1) * this.pageSize
      return list.slice(start, start + this.pageSize)
    }
  },
  watch: {
    filteredUsers() {
      const len = (this.filteredUsers && this.filteredUsers.length) || 0
      const maxPage = Math.max(1, Math.ceil(len / this.pageSize) || 1)
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage
      }
    }
  },
  mounted() {
    this.loadUsers()
    this.loadDepartments()
    this.loadAllRoles()
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    // 检查屏幕尺寸
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    
    normalizeUserList(payload) {
      if (Array.isArray(payload)) return payload
      if (payload && Array.isArray(payload.data)) return payload.data
      if (payload && Array.isArray(payload.content)) return payload.content
      return []
    },
    // 加载用户列表
    loadUsers() {
      request.get('/users')
        .then(response => {
          this.users = this.normalizeUserList(response)
          this.filteredUsers = [...this.users]
        })
        .catch(error => {
          console.error('加载用户列表失败:', error)
          this.$message.error('加载用户列表失败')
        })
    },
    
    // 加载部门列表
    loadDepartments() {
      request.get('/departments')
        .then(response => {
          this.departments = response.data || response
        })
        .catch(error => {
          console.error('加载部门列表失败:', error)
          this.$message.error('加载部门列表失败')
        })
    },
    
    // 搜索用户
    handleSearch() {
      let filtered = [...this.users]
      
      // 按用户名称搜索
      if (this.searchForm.name) {
        filtered = filtered.filter(user => user.name.includes(this.searchForm.name))
      }
      
      // 按手机号搜索
      if (this.searchForm.phone) {
        filtered = filtered.filter(user => user.phone.includes(this.searchForm.phone))
      }
      
      // 按部门搜索
      if (this.searchForm.departmentId) {
        filtered = filtered.filter(user => user.department.id === this.searchForm.departmentId)
      }
      
      this.filteredUsers = filtered
      this.currentPage = 1 // 搜索后回到第一页
    },
    
    // 重置搜索条件
    handleReset() {
      this.searchForm = {
        name: '',
        phone: '',
        departmentId: ''
      }
      this.filteredUsers = [...this.users]
      this.currentPage = 1
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    
    // 当前页码变化
    handleCurrentChange(val) {
      this.currentPage = val
    },
    
    // 选择用户
    handleSelectionChange(selection) {
      this.selectedUsers = selection
    },
    
    // 查看用户
    handleViewUser(user) {
      this.$router.push(`/users/${user.id}`)
    },
    
    // 新增用户
    handleAddUser() {
      this.isEditMode = false
      this.dialogTitle = '新增用户'
      this.resetUserForm()
      this.dialogVisible = true
    },
    
    // 编辑用户
    handleEditUser(user) {
      this.isEditMode = true
      this.dialogTitle = '编辑用户'
      this.userForm = {
        id: user.id,
        name: user.name,
        username: user.username,
        phone: user.phone,
        email: user.email,
        departmentId: user.department.id
      }
      this.dialogVisible = true
    },
    
    // 删除用户
    handleDeleteUser(user) {
      this.$confirm(`确定要删除用户 "${user.name}" 吗？`, '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
      .then(() => {
        request.delete(`/users/${user.id}`)
          .then(() => {
            this.$message.success('删除用户成功')
            this.loadUsers()
          })
          .catch(error => {
            console.error('删除用户失败:', error)
            this.$message.error('删除用户失败')
          })
      })
      .catch(() => {
        this.$message.info('已取消删除')
      })
    },
    
    // 修改密码
    handleChangePassword(user) {
      this.passwordForm.userId = user.id
      this.passwordForm.newPassword = ''
      this.passwordForm.confirmPassword = ''
      this.passwordDialogVisible = true
    },
    
    // 重置用户表单
    resetUserForm() {
      this.userForm = {
        id: '',
        name: '',
        username: '',
        phone: '',
        email: '',
        departmentId: '',
        password: ''
      }
      // 不用 resetFields：避免配合 el-form-item v-if 时 initialValue 被恢复成 null
      this.$refs.userForm && this.$refs.userForm.clearValidate()
    },
    
    // 提交用户表单
    handleSubmit() {
      this.$refs.userForm.validate((valid) => {
        if (valid) {
          if (!this.isEditMode && (this.userForm.password == null || this.userForm.password.trim() === '')) {
            this.$message.error('请输入密码')
            return
          }
          // 转换部门ID为部门对象格式
          const userData = {
            ...this.userForm,
            department: {
              id: this.userForm.departmentId
            }
          }
          // 删除不需要的字段
          delete userData.departmentId
          
          if (this.isEditMode) {
            // 编辑用户
            request.put(`/users/${userData.id}`, userData)
              .then(() => {
                this.$message.success('编辑用户成功')
                this.dialogVisible = false
                this.loadUsers()
              })
              .catch(error => {
                console.error('编辑用户失败:', error)
                this.$message.error('编辑用户失败')
              })
          } else {
            // 新增用户
            request.post('/users', userData)
              .then(() => {
                this.$message.success('新增用户成功')
                this.dialogVisible = false
                this.loadUsers()
              })
              .catch(error => {
                console.error('新增用户失败:', error)
                this.$message.error('新增用户失败')
              })
          }
        }
      })
    },
    
    // 验证密码复杂度：6-20 位，且必须包含字母、数字和符号三种
    validateStrongPassword(rule, value, callback) {
      if (!value) {
        callback(new Error('请输入密码'))
        return
      }
      if (value.length < 6 || value.length > 20) {
        callback(new Error('密码长度需在 6 到 20 个字符之间'))
        return
      }
      const hasLetter = /[A-Za-z]/.test(value)
      const hasNumber = /\d/.test(value)
      const hasSymbol = /[^A-Za-z0-9]/.test(value)
      if (!hasLetter || !hasNumber || !hasSymbol) {
        callback(new Error('密码必须同时包含字母、数字和符号'))
      } else {
        callback()
      }
    },

    // 验证密码一致性
    validateConfirmPassword(rule, value, callback) {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    },

    // 计算密码强度
    calcPasswordStrength(password) {
      if (!password) {
        return { level: 0, text: '无', type: 'none' }
      }
      const length = password.length
      const hasLetter = /[A-Za-z]/.test(password)
      const hasNumber = /\d/.test(password)
      const hasSymbol = /[^A-Za-z0-9]/.test(password)

      const typeCount = [hasLetter, hasNumber, hasSymbol].filter(Boolean).length

      // 强度展示规则必须与下方 validateStrongPassword 保持一致：
      // - 高(强)：字母+数字+符号都要有，并且长度 >= 10（且长度在 6-20 内才算）
      // - 中：至少两类要有，并且长度 >= 6（且长度在 6-20 内才算）
      // - 弱：其它情况
      const lengthOk = length >= 6 && length <= 20

      let level = 1
      let text = '弱'
      let type = 'low'

      if (lengthOk && typeCount === 3 && length >= 10) {
        level = 3
        text = '强'
        type = 'high'
      } else if (lengthOk && typeCount >= 2 && length >= 6) {
        level = 2
        text = '中'
        type = 'medium'
      }

      return { level, text, type }
    },
    
    // 提交密码修改
    handlePasswordSubmit() {
      this.$refs.passwordForm.validate((valid) => {
        if (valid) {
          request.put(`/users/${this.passwordForm.userId}/password`, {
            password: this.passwordForm.newPassword
          })
          .then(() => {
            this.$message.success('密码修改成功')
            this.passwordDialogVisible = false
          })
          .catch(error => {
            console.error('密码修改失败:', error)
            this.$message.error('密码修改失败')
          })
        }
      })
    },

    // 加载全部角色
    loadAllRoles() {
      request.get('/roles')
        .then(res => {
          this.allRoles = res.data || res
        })
        .catch(err => {
          console.error('加载角色列表失败:', err)
        })
    },

    // 打开分配角色弹窗
    openRoleDialog(user) {
      this.roleForm.userId = user.id
      this.roleForm.roleIds = []
      this.roleDialogVisible = true
      request.get(`/users/${user.id}/roles`)
        .then(res => {
          const roles = res.data || res
          this.roleForm.roleIds = (roles || []).map(r => r.id)
        })
        .catch(err => {
          console.error('加载用户角色失败:', err)
          this.$message.error('加载用户角色失败')
        })
    },

    // 提交分配角色
    handleRoleSubmit() {
      if (!this.roleForm.userId) return
      request.put(`/users/${this.roleForm.userId}/roles`, this.roleForm.roleIds)
        .then(() => {
          this.$message.success('分配角色成功')
          this.roleDialogVisible = false
          this.loadUsers()
        })
        .catch(err => {
          console.error('分配角色失败:', err)
          this.$message.error('分配角色失败')
        })
    }
  }
}
</script>

<style scoped>
.user-list-container {
  width: 100%;
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding: 14px 4px 6px;
}

.page-title-block h2 {
  margin: 0;
  font-size: 22px;
  color: #0f8f59;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #80848f;
}

.search-card {
  margin-bottom: 20px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 143, 89, 0.08);
  border: none;
}

.search-inline-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

/* 移动端卡片样式 */
.user-list-mobile {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.user-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 15px;
  transition: all 0.3s ease;
}

.user-card:hover {
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
}

.user-serial {
  margin: 0 0 6px 0;
  font-size: 12px;
  color: #909399;
}

.user-info h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #333;
}

.user-info p {
  margin: 5px 0;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-actions {
  display: flex;
  gap: 8px;
  margin-top: 15px;
  flex-wrap: wrap;
}

/* 角色列标签：强制白字+主题绿底，避免正式环境对比度不足 */
.user-list-container .role-tag.el-tag--success {
  background-color: var(--kh-primary, #17b676) !important;
  border-color: var(--kh-primary, #17b676) !important;
  color: #fff !important;
}

.role-list-box {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 14px;
  background: #f9fafc;
}

.role-list-box .el-checkbox-group {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-gap: 8px 16px;
}

.role-list-box .el-checkbox {
  margin: 0;
}

.role-list-box .el-checkbox__label {
  font-size: 14px;
}

.password-strength {
  margin-top: 6px;
}

.password-strength .strength-bars {
  display: flex;
  gap: 4px;
  margin-bottom: 2px;
}

.password-strength .bar {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background-color: #ebeef5;
  transition: background-color 0.3s;
}

.password-strength .bar.active:nth-child(1) {
  background-color: #f56c6c;
}

.password-strength .bar.active:nth-child(2) {
  background-color: #e6a23c;
}

.password-strength .bar.active:nth-child(3) {
  background-color: #67c23a;
}

.password-strength .strength-text {
  font-size: 12px;
}

.password-strength .strength-text.level-low {
  color: #f56c6c;
}

.password-strength .strength-text.level-medium {
  color: #e6a23c;
}

.password-strength .strength-text.level-high {
  color: #67c23a;
}

.password-rule-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #80848f;
}

@media (max-width: 768px) {
  .user-list-container {
    padding: 0 4px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .search-inline-form {
    flex-direction: column;
    gap: 15px;
  }
  
  .search-inline-form .el-form-item {
    width: 100%;
  }
  
  .search-inline-form .el-input, 
  .search-inline-form .el-select {
    width: 100%;
  }
}
</style>